import { Component, Prop, Vue } from 'vue-property-decorator'
import Chart from 'chart.js/auto'
import ChartDataLabels from 'chartjs-plugin-datalabels'
import { NumberFormat } from '@/types/observation'

// https://github.com/chartjs/Chart.js/issues/10388
const legendHeightPlugin = {
    id: 'increase-legend-spacing',
    beforeInit(chart: any) {
        // Get reference to the original fit function
        const originalFit = chart.legend.fit

        // Override the fit function
        chart.legend.fit = function fit() {
            // Call original function and bind scope in order to use `this` correctly inside it
            originalFit.bind(chart.legend)()
            // Change the height as suggested in another answers
            this.height += 20
        }
    },
}

@Component
export default class BarChart extends Vue {
    @Prop({ default: 0 }) readonly id!: string
    @Prop({ default: [] }) readonly data!: Array<any>
    @Prop({ default: [] }) readonly labels!: Array<string>
    @Prop() dataFormat!: NumberFormat
    @Prop() readonly title!: string
    @Prop() readonly titleX!: string
    @Prop() readonly titleY!: string

    @Prop() readonly height!: string
    @Prop() readonly width!: string

    myChart: any = undefined

    totalByPeriod = {}

    mounted() {
        this.data.forEach((item) => {
            item.data.forEach((_data) => {
                if (this.totalByPeriod[_data.x] !== undefined) {
                    this.totalByPeriod[_data.x] += _data.y
                } else {
                    this.totalByPeriod[_data.x] = _data.y
                }
            })
        })

        this.createChart({
            datasets: [
                ...this.data.map((item) => ({
                    ...item,
                    datalabels: {
                        anchor: 'end',
                        align: 'end',
                        offset: -5,
                    },
                })),
            ],
            labels: this.labels,
        })
    }

    createChart(chartData: object) {
        const canvas: any = document.getElementById(`barchart-${this.id}`)
        // as HTMLCanvasElement
        const options = {
            type: 'bar',
            data: chartData,
            plugins: [ChartDataLabels, legendHeightPlugin],
            options: {
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                    legend: {
                        onHover: function (e, a) {
                            canvas.style.cursor = 'pointer'
                        },
                        onLeave: function (e, a) {
                            canvas.style.cursor = 'default'
                        },
                        labels: {
                            filter: function (item: any, chart: any) {
                                // filter out duplicated labels
                                return (
                                    chart.datasets.findIndex(
                                        (dataset: any) => dataset.label === item.text
                                    ) === item.datasetIndex
                                )
                            },
                        },
                        title: {
                            text: this.title,
                            display: Boolean(this.title),
                        },
                    },
                    datalabels: {
                        display: !!this.dataFormat,
                        color: 'rgb(64 64 64)',
                        font: {
                            weight: 'bold',
                        },
                        formatter: (value: any) => {
                            if (this.dataFormat === 'number') {
                                return value.y
                            } else if (this.dataFormat === 'percent') {
                                if (!this.totalByPeriod[value.x]) {
                                    return '0%'
                                }
                                return `${((value.y / this.totalByPeriod[value.x]) * 100).toFixed(
                                    1
                                )}%`
                            }
                        },
                    },
                    tooltip: {
                        boxPadding: 4,
                    },
                },
                scales: {
                    x: {
                        title: {
                            ...(this.titleX && { display: true }),
                            ...(this.titleX && { text: this.titleX }),
                        },
                    },
                    y: {
                        title: {
                            ...(this.titleY && { display: true }),
                            ...(this.titleY && { text: this.titleY }),
                        },
                    },
                },
                onClick: (event, elements, chart) => {
                    this.$emit('elClicked', elements[0]?.datasetIndex, elements[0]?.index)
                },
            },
        }

        // @ts-ignore -- TS types mismatch
        this.myChart = new Chart(canvas, options)
    }

    beforeDestroy() {
        this.myChart?.destroy()
    }
}
